<template>
  <div id="doc-finder-app">
    <div
      class="search-wrapper layout-wrapper"
      :style="'background-color:' + config.searchBackground"
    >
      <div class="content-wrapper">
        <div class="search-outer-container">
          <div class="search-container">
            <input
              type="text"
              id="query-input"
              :placeholder="labels.searchPlaceholder"
              v-model="currentQuery"
              @keyup="onKeyUp"
              aria-labelledby="query-input"
            />
            <a class="icon-link icon-link-clear" @click="clearQuery"></a>
            <div class="vertical-separator"></div>
            <a class="icon-link icon-link-search" @click="performSearch">
              <span>{{labels.searchButton}}</span>
            </a>
          </div>
        </div>
        <div class="search-description" v-html="labels.searchDescription"></div>
      </div>
    </div>
    <div class="results-wrapper layout-wrapper">
      <div class="content-wrapper">
        <div class="results-container">
          <div class="result-count">
            <span v-if="results && results.length > 0 && results.length < 100">{{getResultCount()}}</span>
            <span v-if="results && results.length >= 100">{{labels.tooManyResults}}</span>
          </div>
          <accordion v-if="results && results.length > 0">
            <accordion-item
              v-for="item in pagedResults"
              :key="item.id"
              :expanded="item.expanded"
              @toggle="onProductToggled"
              :value="item.id"
            >
              <template v-slot:header>
                <product-header :product="item"></product-header>
              </template>
              <template v-slot:content>
                <product-details :product="item" v-if="item.loaded"></product-details>
              </template>
            </accordion-item>
          </accordion>
        </div>
        <div class="pager-container" v-if="results.length > pagedResults.length">
          <a class="pager" @click="onShowMore">{{labels.showMore}}</a>
        </div>
        <div
          class="no-results"
          v-if="!isInitialState && (!results || results.length === 0)" v-html="labels.noResults"
        ></div>
        <div style="display:none">
          <DownloadForm ref="downloadForm"></DownloadForm>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Config from "./services/config.js";
import Labels from "./services/labels.js";
import Utils from "./services/utils.js";
import DataLayer from "./services/datalayer.js";
import Accordion from "./components/Accordion.vue";
import AccordionItem from "./components/AccordionItem.vue";
import ProductHeader from "./components/ProductHeader.vue";
import ProductDetails from "./components/ProductDetails.vue";
import DownloadForm from "./components/DownloadForm.vue";

/* Import Asset List styles */
import "../public/styles/components/asset-list.css";

export default {
  name: "app",
  components: {
    Accordion,
    AccordionItem,
    ProductHeader,
    ProductDetails,
    DownloadForm
  },
  data() {
    return {
      labels: Labels,
      config: Config,
      isLoading: false,
      currentQuery: "",
      results: [],
      pagedResults: [],
      isInitialState: true
    };
  },
  created() {
    console.log("Initializing application");
    Config.init();
    Labels.init();

    let self = this;
    let params = window.location.href.substring(
      window.location.href.indexOf("?") + 1
    );
    if (params.length > 0) {
      params.split("&").forEach(function(item) {
        let pair = item.split("=");
        if (pair.length == 2) {
          let key = pair[0].toLowerCase();
          if (key == "q") {
            self.currentQuery = decodeURIComponent(pair[1]);
          }
        }
      });
    }
  },
  mounted() {
    if (this.currentQuery) {
      this.performSearch();
    }
  },
  methods: {
    performSearch() {
      this.isInitialState = true;
      this.results = [];
      if (!this.currentQuery || this.currentQuery.length < 3) {
        return;
      }
      let loader = Utils.showLoader(".results-wrapper");
      DataLayer.search(this.currentQuery)
        .then(res => {
          let products = res.data;
          products.map(p => {
            p.expanded = false;
            p.loaded = false;
          });
          this.results = products;
          //if we only have one result, we expand it by default
          if (this.results.length === 1) {
            //this.results[0].expanded = true;
            //this.results[0].loaded = true;
            this.onProductToggled(null, this.results[0].id);
          }
          if (
            Config.productsPerPage > 1 &&
            this.results.length > Config.productsPerPage
          ) {
            this.pagedResults = this.results.slice(0, Config.productsPerPage);
          } else {
            this.pagedResults = this.results;
          }
          Utils.removeLoader(loader);
          Utils.reportToAnalytics(
            "DocFinder SDS Product Search",
            this.currentQuery
          );
          this.isInitialState = false;
        })
        .catch(err => {
          Utils.showStatusMessage(`${Labels.errorDuringSearch}. ${err}`);
          Utils.removeLoader(loader);
          this.isInitialState = false;
        });
    },
    onKeyUp(event) {
      event.preventDefault();
      if (event.key == "Enter") {
        console.log("Starting search...");
        this.performSearch();
      }
    },
    clearQuery() {
      this.currentQuery = "";
      var el = document.getElementById("query-input");
      if (el) el.focus();
    },
    onProductToggled(sender, productId) {
      this.results.map(p => {
        if (p.id === productId) {
          p.expanded = !p.expanded;
          if (p.expanded && !p.loaded) {
            //we use this to lazy load product info
            p.loaded = true;
          }
        } else {
          p.expanded = false;
        }
      });
    },
    onShowMore() {
      if (
        this.results.length === 0 ||
        this.results.length <= this.pagedResults.length
      )
        return;
      let end = Math.min(
        this.pagedResults.length + Config.productsPerPage,
        this.results.length
      );
      this.pagedResults = this.results.slice(0, end);
    },
    getResultCount() {
      return Labels.resultCount.replace("{count}", this.results.length);
    }
  }
};
</script>

<style>
#doc-finder-app {
  font-family: "Gotham SSm A", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
}
.search-container {
  width: 600px;
  background-color: #e4e4e4;
  padding: 4px 16px;
  box-sizing: border-box;
  display: flex;
}

.vertical-separator {
  width: 1px;
  min-width: 1px;
  background-color: black;
  height: 40px;
}

.search-container input {
  border: none;
  flex-grow: 1;
  background-color: #e4e4e4;
  font-size: 16px;
}

.search-container input::-webkit-search-cancel-button {
  position: relative;
  right: 20px;
}

.search-container input::-ms-clear {
  display: none;
}

.search-container .icon-link-search {
  padding-left: 34px;
  background-size: 24px 24px;
  background-position-y: 50%;
  background-position-x: 10px;
}

.icon-link-search {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAAEgBckRAAAAAXNSR0IArs4c6QAAAx1JREFUaAXtmDuOFDEQhhdESAAxxBAQkhFwDsjhCiDtsrscADIOA0KMhgiuABIHIIZog4H6RP+9nvKr29s9o0FdkmW7Hn+Vq93Vdh8dDaA/pkPboohxfUtcmUTWFX0n3ti8CNELFdUth7DrKRGvA6fE0wcZ8PthShjxrvXq/waRgrG9jjNJT1NAac39cm+Ye2JVe1cLxy+M+cOc0UcTPEsIPUiv8s1G9/vZ5SBrgIoXfjfe+0vbeLTqjDAMW6xZ4MiwoBKLmoyOY5xD4dyzQNkEv6zxbF9YuzL5PfvcEG9aY+PgTEnmMY8mQARQMn48UC/CIBU4IOIaDQkkwiDHGJKKGjU5AJQ6IWNS4Yl3UHIvGzWntggo7D90KCEvC9xUwh0ajkRT4Akr6rWiA37tozUtjCkywJtNFaWE8HZTXSeh8G3W9lO/MQ9U3GaiDAuMqKmwFD/Ktvj0TU7C8pA7oq47R6xkNCnCVJELwaSXOr2EetFYhpHAMThHoTukrG+ZDnXAM0GX3VUkXQmKSgnhnY73MyErsoaugNSg+7KIlhB+6gx5uXKkCwcOmkirSDlhd0n+pgndjG4HIALz/UWn89r6ZlK6PPijDlH882YPFUOA5eS0otssPgucvGpGqRgSvVZyUtFtFhO9nDSD1Axni77meJEvGVgyMDYDHJxydVy1IuzRxWavxBduZS0MrGUMBlg7pdQhm7MA3/4aoRNeqbXo6u+6GvBQud8qBJ47b5cwsfELAXtW0h8RZY1z4lVJZ01hTvJnKxcU2ZYj+tGXjARw+KMLzNSxNGHWxuJOES5gyM+vmifdV4T7u2ZQk5fuNV+d8RM3b5k+dUZf3HzSqX/cZG1I5ckFga0yr/5BTnkqPh+gjXO8tvmYSoTuZ4cBpi4vNpyX+M+xsqashT1VhYs+T4v9TWMMz1cc2V2Y7K61nRML4QPkn4gCK/XYvLVG4F7v3Hh7ITJNHSfTlEOqFlXlhzV4/FdI7XEC9otgfmbtoIiAUws5PahVWLAEnFrIbPf4uRIUXt3DBZ3M5XAuXALWAo7ncrLgLhn4XzLwF/SxQVh5o+YKAAAAAElFTkSuQmCC");
}

.icon-link {
  padding: 12px 10px 12px 34px;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  background-position-y: 50%;
  background-position-x: 10px;
  font-family: "Gotham SSm A", Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 14px;
  color: black;
  font-weight: 700;
  cursor: pointer;
}

a.icon-link span {
  margin-left: 5px;
}

.cla-messagebox {
  position: fixed;
  top: 100px;
  right: 0;
  background-color: #ffffff;
  max-width: 400px;
  padding: 20px;
  font-family: "Gotham SSm A", Arial, Helvetica, sans-serif;
  box-shadow: 3px 3px rgba(200, 200, 200, 0.4);
  border-left: 1px solid #e4e4e4;
  animation: slide 0.5s forwards;
  transition: opacity 500ms linear;
  opacity: 1;
  z-index: 10001;
}

@keyframes slide {
  0% {
    right: 0;
  }
  100% {
    right: 100px;
  }
}

.cla-messagebox.green {
  border-top: 3px solid green;
}

.cla-messagebox.yellow {
  border-top: 3px solid yellow;
}

.cla-messagebox.red {
  border-top: 3px solid red;
}

.loader {
  width: 64px;
  height: 64px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg class='lds-spinner' width='64px' height='64px' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid' style='background: none;'%3E%3Cg transform='rotate(0 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.9166666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(30 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.8333333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(60 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.75s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(90 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.6666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(120 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5833333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(150 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(180 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.4166666666666667s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(210 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.3333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(240 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.25s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(270 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.16666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(300 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.08333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(330 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='0s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3C/svg%3E");
  margin: auto;
}

.loader.top {
  top: 80px;
  bottom: unset;
}

.pager-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.pager {
  font-family: "Gotham SSm A", Arial, Helvetica, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  background-image: url("data:image/svg+xml;charset=utf8,%3C?xml version='1.0' encoding='utf-8'?%3E%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 10 6'%3E%3Cpath fill='%232655FF' d='M9.5,0.2C9.4,0.1,9.2,0.1,9.1,0.1C9,0.1,8.8,0.1,8.7,0.2L5,3.9L1.3,0.2C1.2,0.1,1,0.1,0.9,0.1S0.6,0.1,0.5,0.2 L0.2,0.5C0,0.8,0,1.1,0.2,1.3l4.4,4.4C4.7,5.9,4.9,5.9,5,5.9h0c0.1,0,0.3-0.1,0.4-0.2l4.4-4.4C9.9,1.2,10,1.1,10,0.9 c0-0.1-0.1-0.3-0.2-0.4L9.5,0.2z M5,5.8L5,5.8L5,5.8L5,5.8z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 0 3px;
  background-size: 16px 16px;
  padding-left: 25px;
  cursor: pointer;
  color: #323232;
}

.results-container {
  position: relative;
}

.no-results {
  text-align: left;
}

.result-count {
  font-weight: 700;
  margin-bottom: 10px;
}

.search-wrapper {
  margin-bottom: 20px;
}

.search-wrapper > div {
  margin: 0 auto 20px auto;
}

.search-wrapper .search-description {
  padding: 20px 0 0 0;
  font-size: 12px;
}

.results-wrapper {
  min-height: 150px;
  position:relative;
  margin-bottom: 40px;
}


@media all and (max-width: 780px) {
  .search-wrapper > div {
    padding: 0 15px;
  }

  .search-wrapper .search-outer-container {
    width: calc(100% - 30px);
  }

  .search-wrapper .search-outer-container .search-container {
    width: 100%;
  }

  .results-wrapper .content-wrapper {
    margin: 0 15px;
    box-sizing: border-box;
    padding: 0 !important;
  }

  .search-container .icon-link span {
    display: none;
  }

.search-container input {
  font-size: 14px;
}
}
</style>
